import { DatePipe } from '@angular/common';
import {
  TransactionChannel,
  TransactionProcessor,
  TransactionStatus,
} from '@migo-apps/utils';
import { DateRange } from '@shared/models';
import { SortOptions } from './sort-options';

/* It's a class that holds the values of the search form and provides methods to
convert the values to an object, an array, and a query string */
export class SearchOptions {
  range?: DateRange | undefined;
  reference?: string | undefined;
  status?: TransactionStatus[] | undefined;
  user?: string | undefined;
  processor?: TransactionProcessor[] | undefined;
  channel?: TransactionChannel[] | undefined;
  orderBy: SortOptions | undefined;
  textSearch: string | undefined;
  minAmount: number | undefined;
  maxAmount: number | undefined;
  currencyAmount: string | undefined;
  createdBy?: string;

  /**
   * It takes an object with the same properties as the class, and sets the class
   * properties to the values of the object properties
   * @param {any} v - any - this is the object that is passed to the function.
   */
  setOptions(v: any) {
    this.range = v.range;
    this.reference = v.reference;
    this.status = v.status;
    this.user = v.user;
    this.processor = v.processor;
    this.channel = v.channel;
    this.orderBy = v.orderBy;
    this.textSearch = v.textSearch;
    this.minAmount = v.minAmount;
    this.maxAmount = v.maxAmount;
    this.createdBy = v.createdBy;
  }

  /**
   * It removes an option from the object
   * @param {string} option - The name of the option to remove.
   * @returns The object itself.
   */
  removeOption(option: string) {
    (this as any)[option] = undefined;
    return this;
  }

  /**
   * It takes the values of the form and converts them into a format that can be sent
   * to the API
   * @returns An object
   */
  toParams() {
    const params: any = {};

    if (this.range?.from && this.range?.to) {
      params.from = this.formatDate(this.range?.from as Date) + ' 00:00:00';
      params.to = this.formatDate(this.range?.to as Date) + ' 23:59:59';
    }

    if (this.user) {
      params.userId = this.user;
    }

    if (this.status?.length) {
      params.status = this.status?.map((status) => status.key).join(',');
    }

    if (this.processor?.length) {
      params.processor = this.processor
        ?.map((processor) => processor.key)
        .join(',');
    }

    if (this.channel?.length) {
      params.channel = this.channel?.map((channel) => channel.key).join(',');
    }

    if (this.reference) {
      params.reference = this.reference;
    }

    if (this.orderBy) {
      params.sortField = this.orderBy.sortField;
      params.sortOrder = this.orderBy.sortOrder;
    }

    if (this.textSearch) {
      params.textSearch = this.textSearch;
    }

    if (this.minAmount) {
      params.minAmount = this.minAmount;
    }

    if (this.maxAmount) {
      params.maxAmount = this.maxAmount;
    }

    if (this.createdBy) {
      params.createdBy = this.createdBy;
    }

    return params;
  }

  /**
   * It returns an object with the same properties as the current instance of the
   * class
   * @returns An object with the properties of the class.
   */
  toJSON() {
    return {
      range: this.range || { from: null, to: null },
      reference: this.reference || '',
      status: this.status || '',
      user: this.user || '',
      processor: this.processor || '',
      channel: this.channel || '',
    };
  }

  /**
   * It takes the values of the form and returns an array of objects with the key and
   * value of each form control
   * @returns An array of objects with key and value properties.
   */
  toArray() {
    let array = [
      { key: 'reference', value: this.reference },
      { key: 'user', value: this.user },
      { key: 'textSearch', value: this.textSearch },
    ];

    const status = this.status?.length
      ? this.status?.map((s) => s?.name).join(',')
      : '';

    const processor = this.processor?.length
      ? this.processor?.map((s) => s?.name).join(',')
      : '';

    const channel = this.channel?.length
      ? this.channel?.map((s) => s?.name).join(',')
      : '';

    array.push({
      key: 'status',
      value: status,
    });

    array.push({
      key: 'processor',
      value: processor,
    });

    array.push({
      key: 'channel',
      value: channel,
    });

    array = array.filter((option) => !!option.value);

    const fromDate = new DatePipe('en-US').transform(
      this.range?.from,
      'yyyy-MM-dd'
    );
    const toDate = new DatePipe('en-US').transform(
      this.range?.to,
      'yyyy-MM-dd'
    );

    if (fromDate && toDate) {
      array.unshift({ key: 'range', value: fromDate + ' - ' + toDate });
    }

    return array;
  }

  /**
   * It takes a Date object and returns a string in the format YYYY-MM-DD
   * @param {Date} d - Date - the date to format
   * @returns A string in the format of yyyy-mm-dd
   */
  private formatDate(d: Date) {
    const yyyy = d.getFullYear();
    const mm = d.getMonth() + 1;
    const dd = d.getDate();

    return (
      yyyy + '-' + (mm < 10 ? `0${mm}` : mm) + '-' + (dd < 10 ? `0${dd}` : dd)
    );
  }
}
